import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { loanActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { InsuranceApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { RefreshEvents } from '../../../common/SharedEvents';
import { LoadPanel } from 'devextreme-react/load-panel';
import { CommonLoanSummary } from '../../common/LoanComponents';
import { InsuranceGrid } from '../common/InsuranceComponents';
import { LoanType } from '../../common/LoanConstants';
import { setInsurancesForLoanType } from '../common/InsuranceFunctions';
import { handleAdminError, handleAdminSuccess } from '../../../common/SharedFunctions';
import { CommonLoanSummaryInvokingFeatureType } from '../../common/constants/CommonLoanSummaryInvokingFeatureType';

const PropertyInsuranceModal = () => {
    const { isOpen, loanId, propertyId } = useSelector((state) => state.loan.propertyInsuranceModal);
    const { dispatch } = store;

    const emptyInsuranceGroup = {
        allGood: true,
        notes: null,
        insurances: [],
    };

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        form: { paddingLeft: '5px', paddingTop: '10px' },
        formRow: { marginBottom: '0' },
    };

    const [insuranceGroup, set_insuranceGroup] = useState(emptyInsuranceGroup);
    const [loan, set_loan] = useState(null);
    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const insuranceApi = new InsuranceApi.InsuranceApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const _handleClose = () =>
        dispatch(
            loanActions.setPropertyInsuranceModal({
                propertyInsuranceModal: { isOpen: false },
            })
        );

    useEffect(() => {
        async function loanOrPropertyChanged() {
            if (loanId && loanId > 0 && propertyId && propertyId > 0) {
                await loadLoanInfo(loanId, propertyId);
            } else {
                resetState();
            }
            set_loadingOpen(false);
        }

        loanOrPropertyChanged();
    }, [isOpen, loanId, propertyId]);

    const resetState = () => {
        //set_id(0);
        set_loan(null);
    };

    const setInsuranceGroup = (insGroup, loanTypeId, isRental) => {
        insGroup = insGroup ?? emptyInsuranceGroup;
        //Build Rent exception
        const lt = isRental ? LoanType.Rental : loanTypeId;
        insGroup.loanTypeId = lt;
        insGroup.insurances = setInsurancesForLoanType(insGroup.insurances, lt, insGroup.inFloodZone);
        set_insuranceGroup({ ...insGroup });
    };

    const loadLoanInfo = async (loanId, propertyId) => {
        set_loadingMessage('Loading...');
        set_loadingOpen(true);

        const response = await insuranceApi.getPropertyInsurances(loanId, propertyId);
        if (response.statusCode !== 200) {
            set_loadingOpen(false);
            return;
        }
        const loan = response.result;
        set_loan(loan);
        setInsuranceGroup(loan.insuranceGroup, loan.loanTypeId, loan.isRental ?? false);

        set_loadingOpen(false);
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const model = insuranceGroup;

            const response = await insuranceApi.setPropertyInsurances(loanId, propertyId, model);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Loan insurances saved successully.');

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const onInsuranceChange = (insGroup) => {
        set_insuranceGroup({ ...insGroup });
    };

    if (!isOpen || !loan) {
        return '';
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '1250px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <form
                                    name='loanLostForm'
                                    onSubmit={(event) => onSubmit(event)}
                                >
                                    <div>
                                        <div
                                            className='page-header'
                                            role='banner'
                                        >
                                            <h1 style={{ whiteSpace: 'nowrap' }}>Insurances</h1>
                                            <button
                                                type='button'
                                                className='close'
                                                data-dismiss='modal'
                                                aria-label='Close'
                                                onClick={_handleClose}
                                            >
                                                <span aria-hidden='true'>&times;</span>
                                            </button>
                                        </div>
                                        <div>
                                            <div
                                                style={styles.form}
                                                className={'standard-form'}
                                            >
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div
                                                        className='columns small-12 large-12'
                                                        style={{
                                                            paddingBottom: '0',
                                                        }}
                                                    >
                                                        <InsuranceGrid
                                                            insuranceGroup={insuranceGroup}
                                                            loanTypeId={loan.loanTypeId}
                                                            committedAmount={loan.commitment}
                                                            onChange={onInsuranceChange}
                                                            enabled={true}
                                                        />
                                                    </div>
                                                </div>
                                                <CommonLoanSummary
                                                    loan={loan}
                                                    invokingFeature={CommonLoanSummaryInvokingFeatureType.PropertyInsurance}
                                                />
                                            </div>
                                        </div>
                                        <div style={{ padding: '5px 5px' }}>
                                            <div
                                                className='button-footer'
                                                style={styles.buttonContainer}
                                            >
                                                <button
                                                    style={styles.buttonMarginRight}
                                                    type='submit'
                                                    className='button primary'
                                                >
                                                    Submit
                                                </button>
                                                <button
                                                    type='button'
                                                    className='button hollow'
                                                    data-dismiss='modal'
                                                    onClick={_handleClose}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
        </>
    );
};

export default PropertyInsuranceModal;
